import React from "react";
import { useTranslation } from "react-i18next";
import type {
  TransformedOrderTotals, // Use TransformedOrderTotals
  TransformedOrderCoupon,
} from "../types/orderApiTypes";

interface OrderTotalsDisplayProps {
  totals: TransformedOrderTotals; // Receive the transformed totals object
  coupons: TransformedOrderCoupon[];
}

const OrderTotalsDisplay: React.FC<OrderTotalsDisplayProps> = ({
  totals,
  coupons,
}) => {
  const { t } = useTranslation();
  // Currency symbol is now part of the totals object
  const currencySymbol = totals.currencySymbol || "₺";

  // Ensure totals object is valid (basic check)
  if (!totals) {
    console.error("OrderTotalsDisplay: Invalid totals object received", totals);
    return null; // Or render a placeholder/error
  }

  // Use numeric values from transformed totals for checks
  const hasDiscount = totals.totalDiscount > 0;
  const isShippingFree = totals.totalShipping === 0; // Check if shipping is free
  const hasShippingCost = totals.totalShipping > 0; // Check if there's a shipping cost
  const hasTax = totals.totalTax > 0;

  // Use pre-formatted strings from the totals object
  const {
    formattedSubtotal,
    formattedTotalDiscount,
    formattedTotalShipping,
    formattedTotalTax,
    formattedTotalPrice,
  } = totals;

  return (
    <div className="order-totals-section">
      <h3>{t("cart.totalsTitle", "Order Totals")}</h3>
      <table className="order-totals-table">
        <tbody>
          <tr>
            <th>{t("cart.totals.subtotal", "Subtotal")}:</th>
            <td>
              {formattedSubtotal}
              {currencySymbol}
            </td>
          </tr>
          {hasDiscount && (
            <>
              {coupons.map((coupon) => (
                <tr key={coupon.code} className="discount-row">
                  <th>
                    {t("cart.couponApplied", "Coupon: {{code}}", {
                      code: coupon.code,
                    })}
                    :
                  </th>
                  <td>
                    {/* Display the formatted discount from the coupon object */}
                    -{coupon.formattedDiscountAmount}
                    {currencySymbol}
                  </td>
                </tr>
              ))}
              {/* Only show generic discount if specific coupons aren't listed or don't cover the full amount */}
              {!coupons.length && (
                <tr className="discount-row">
                  <th>{t("order.totalDiscount", "Discount")}:</th>
                  <td>
                    -{formattedTotalDiscount}
                    {currencySymbol}
                  </td>
                </tr>
              )}
            </>
          )}
          {/* --- Modified Shipping Display --- */}
          {isShippingFree && ( // Show "Free Shipping" if cost is 0
            <tr className="shipping-row free-shipping-row">
              <th className="free-shipping-label">
                {t("cart.totals.freeShipping", "Free Shipping")}:
              </th>
              {/* Display 0 with currency symbol */}
              <td>0.00{currencySymbol}</td>
            </tr>
          )}
          {hasShippingCost && ( // Show shipping cost if > 0
            <tr className="shipping-row">
              <th>{t("cart.totals.otherShipping", "Shipping")}:</th>
              <td>
                {formattedTotalShipping}
                {currencySymbol}
              </td>
            </tr>
          )}
          {/* --- End Modified Shipping Display --- */}
          {hasTax && (
            <tr>
              <th>{t("order.tax", "Tax")}:</th>
              <td>
                {formattedTotalTax}
                {currencySymbol}
              </td>
            </tr>
          )}
          <tr className="grand-total-row">
            <th>{t("cart.totals.total", "Total")}:</th>
            <td>
              <strong>
                {formattedTotalPrice}
                {currencySymbol}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default OrderTotalsDisplay;
